<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { useQaPermissions } from 'dashboard/composables/useQaPermissions';
import { useQaRequest } from 'dashboard/composables/useQaRequest';
import { useQaPagination } from 'dashboard/composables/useQaPagination';
import { useQaSavedViews } from 'dashboard/composables/useQaSavedViews';
import { confirmQaDestructive } from 'dashboard/composables/useQaConfirm';
import QaDashboardApi from 'dashboard/api/qaDashboard';
import { parseListPayload } from 'dashboard/helper/qaApiSchema';
import { downloadQaExportBlob } from 'dashboard/helper/qaExportDownload';
import { dynamicTime, messageTimestamp } from 'shared/helpers/timeHelper';

// QA_FE_KEYWORD_I18N_V1 + QA_FE_FOUNDATION_V1 + QA_FE_CONFIRM_V1
const { t } = useI18n();
const showAlert = useAlert;
const { accountScopedRoute, accountId } = useAccount();
const currentUser = useMapGetter('getCurrentUser');
const { canViewSensitiveEvidence } = useQaPermissions();
const { isLoading, run: runRequest } = useQaRequest();
const {
  page,
  perPage,
  meta,
  applyMeta,
  goPage: goPaginationPage,
} = useQaPagination({ defaultPerPage: 25 });

const savedViewsStorageKey = () => {
  const uid = currentUser.value?.id || 0;
  return `qa_keyword_search_saved_views:${accountId.value || 0}:${uid}`;
};
const {
  views: savedViews,
  saveView,
  removeView,
} = useQaSavedViews({
  storageKey: savedViewsStorageKey,
});

const keyword = ref('');
const isExporting = ref(false);
const rows = ref([]);
const agentBreakdown = ref([]);
const selectedAgentId = ref('');
const inboxId = ref('');
const teamId = ref('');
const fromDate = ref('');
const toDate = ref('');
const newViewName = ref('');

const normalizedKeyword = computed(() => String(keyword.value || '').trim());
const totalMatches = computed(() => Number(meta.value.total_count || 0));

const currentFilters = () => ({
  keyword: keyword.value,
  selectedAgentId: selectedAgentId.value,
  inboxId: inboxId.value,
  teamId: teamId.value,
  fromDate: fromDate.value,
  toDate: toDate.value,
});

const formatMatchedAt = unixTime => {
  const timestamp = Number(unixTime || 0);
  if (!timestamp) return '-';
  return `${messageTimestamp(timestamp, 'MMM d, h:mm a')} (${dynamicTime(timestamp)})`;
};

const buildConversationRoute = ({ conversationDisplayId, messageId }) => {
  const displayId = conversationDisplayId;
  if (!displayId) return null;

  return accountScopedRoute(
    'inbox_conversation',
    { conversation_id: displayId },
    messageId ? { messageId } : {}
  );
};

const saveCurrentView = () => {
  const name = String(newViewName.value || '').trim();
  if (!name) {
    showAlert(t('QA_DASHBOARD.KEYWORD.ERROR_VIEW_NAME'));
    return;
  }
  saveView(name, currentFilters());
  newViewName.value = '';
  showAlert(t('QA_DASHBOARD.KEYWORD.SUCCESS_VIEW_SAVED'));
};

const applySavedView = view => {
  const filters = view?.filters || {};
  keyword.value = filters.keyword || '';
  selectedAgentId.value = filters.selectedAgentId || '';
  inboxId.value = filters.inboxId || '';
  teamId.value = filters.teamId || '';
  fromDate.value = filters.fromDate || '';
  toDate.value = filters.toDate || '';
  if (normalizedKeyword.value) runSearch(1);
};

const deleteSavedView = name => {
  if (
    !confirmQaDestructive({
      action: 'delete saved view',
      subject: name,
    })
  ) {
    return;
  }
  removeView(name);
};

const runSearch = async (nextPage = 1) => {
  if (!normalizedKeyword.value) return;

  page.value = nextPage;
  try {
    await runRequest(async () => {
      const response = await QaDashboardApi.searchKeywords({
        q: normalizedKeyword.value,
        page: page.value,
        per_page: perPage.value,
        inbox_id: inboxId.value || undefined,
        team_id: teamId.value || undefined,
        agent_id: selectedAgentId.value || undefined,
        from: fromDate.value || undefined,
        to: toDate.value || undefined,
      });
      const parsed = parseListPayload(response?.data || {});
      if (!parsed.ok) throw new Error(parsed.error || 'Invalid list payload');
      applyMeta(parsed.meta);
      agentBreakdown.value = response?.data?.agent_breakdown || [];
      rows.value = parsed.items.map(item => ({
        id: item.id,
        conversationId: item.conversation_display_id || item.conversation_id,
        conversationDisplayId: item.conversation_display_id,
        messageIdLabel: item.message_id || '-',
        agentName: item.agent_name || t('QA_DASHBOARD.KEYWORD.UNKNOWN_AGENT'),
        preview: item.preview || '-',
        createdAt: Number(item.created_at || 0),
        matchedAt: formatMatchedAt(item.created_at),
        conversationRoute: buildConversationRoute({
          conversationDisplayId: item.conversation_display_id,
          messageId: item.message_id,
        }),
      }));
      return parsed;
    });
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        error.message ||
        t('QA_DASHBOARD.KEYWORD.ERROR_RUN_SEARCH')
    );
  }
};

const exportCsv = async () => {
  if (!normalizedKeyword.value) return;
  isExporting.value = true;
  try {
    const response = await QaDashboardApi.exportKeywordSearch({
      q: normalizedKeyword.value,
      inbox_id: inboxId.value || undefined,
      team_id: teamId.value || undefined,
      agent_id: selectedAgentId.value || undefined,
      from: fromDate.value || undefined,
      to: toDate.value || undefined,
    });
    await downloadQaExportBlob(response, {
      filename: `qa-keyword-search-${Date.now()}.csv`,
      expectedMime: 'text/csv',
    });
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        error.message ||
        t('QA_DASHBOARD.KEYWORD.ERROR_EXPORT_CSV')
    );
  } finally {
    isExporting.value = false;
  }
};

const setSelectedAgent = agentId => {
  selectedAgentId.value = String(agentId || '');
  runSearch(1);
};

const goPage = nextPage => {
  if (!goPaginationPage(nextPage)) return;
  runSearch(page.value);
};

onMounted(() => {});
</script>

<template>
  <div class="qa-page">
    <!-- QA_FE_LAYOUT_CLEAN_V1 -->
    <div class="qa-shell">
      <div class="qa-header">
        <div class="qa-header-left">
          <h1 class="qa-title">{{ t('QA_DASHBOARD.KEYWORD.TITLE') }}</h1>
        </div>
        <div class="qa-header-actions">
          <button
            v-if="canViewSensitiveEvidence"
            type="button"
            class="qa-btn qa-btn--ghost"
            :disabled="isExporting || !normalizedKeyword"
            @click="exportCsv"
          >
            {{
              isExporting
                ? t('QA_DASHBOARD.KEYWORD.BTN_EXPORTING')
                : t('QA_DASHBOARD.KEYWORD.BTN_EXPORT_CSV')
            }}
          </button>
        </div>
      </div>

      <div class="qa-subheader qa-subheader--tight">
        <p class="qa-desc">{{ t('QA_DASHBOARD.KEYWORD.DESCRIPTION') }}</p>
      </div>

      <div class="qa-toolbar">
        <div class="qa-search-row">
          <label class="qa-field qa-field--grow">
            <span class="qa-label">{{
              t('QA_DASHBOARD.KEYWORD.INPUT_LABEL')
            }}</span>
            <input
              v-model="keyword"
              class="qa-input"
              :placeholder="t('QA_DASHBOARD.KEYWORD.INPUT_PLACEHOLDER')"
              @keydown.enter="runSearch(1)"
            />
          </label>
          <button
            type="button"
            class="qa-btn qa-btn--primary"
            :disabled="isLoading || !normalizedKeyword"
            @click="runSearch(1)"
          >
            {{
              isLoading
                ? t('QA_DASHBOARD.FORM.RUNNING')
                : t('QA_DASHBOARD.KEYWORD.SEARCH')
            }}
          </button>
        </div>

        <details class="qa-disclosure" style="width: 100%; margin-top: 8px">
          <summary>{{ t('QA_DASHBOARD.KEYWORD.FILTERS') }}</summary>
          <div class="qa-toolbar-left" style="padding-top: 8px">
            <label class="qa-field" style="min-width: 140px">
              <span class="qa-label">{{
                t('QA_DASHBOARD.KEYWORD.INBOX_ID')
              }}</span>
              <input
                v-model="inboxId"
                class="qa-input"
                :placeholder="t('QA_DASHBOARD.KEYWORD.OPTIONAL')"
              />
            </label>
            <label class="qa-field" style="min-width: 140px">
              <span class="qa-label">{{
                t('QA_DASHBOARD.KEYWORD.TEAM_ID')
              }}</span>
              <input
                v-model="teamId"
                class="qa-input"
                :placeholder="t('QA_DASHBOARD.KEYWORD.OPTIONAL')"
              />
            </label>
            <label class="qa-field" style="min-width: 160px">
              <span class="qa-label">{{ t('QA_DASHBOARD.KEYWORD.FROM') }}</span>
              <input v-model="fromDate" class="qa-input" type="date" />
            </label>
            <label class="qa-field" style="min-width: 160px">
              <span class="qa-label">{{ t('QA_DASHBOARD.KEYWORD.TO') }}</span>
              <input v-model="toDate" class="qa-input" type="date" />
            </label>
            <label class="qa-field" style="min-width: 200px">
              <span class="qa-label">{{
                t('QA_DASHBOARD.KEYWORD.AGENT_BREAKDOWN')
              }}</span>
              <select
                class="qa-select"
                :value="selectedAgentId"
                @change="setSelectedAgent($event.target.value)"
              >
                <option value="">
                  {{ t('QA_DASHBOARD.KEYWORD.ALL_AGENTS') }} ({{
                    totalMatches
                  }})
                </option>
                <option
                  v-for="agent in agentBreakdown"
                  :key="agent.agent_id || agent.agent_name"
                  :value="agent.agent_id || ''"
                >
                  {{ agent.agent_name }} ({{ agent.count }})
                </option>
              </select>
            </label>
            <label class="qa-field" style="min-width: 220px">
              <span class="qa-label">{{
                t('QA_DASHBOARD.KEYWORD.SAVE_VIEW')
              }}</span>
              <div class="qa-actions">
                <input
                  v-model="newViewName"
                  class="qa-input"
                  :placeholder="t('QA_DASHBOARD.KEYWORD.NAME_PLACEHOLDER')"
                />
                <button
                  type="button"
                  class="qa-btn qa-btn--sm"
                  @click="saveCurrentView"
                >
                  {{ t('QA_DASHBOARD.KEYWORD.SAVE') }}
                </button>
              </div>
            </label>
          </div>
          <div
            v-if="savedViews.length"
            class="qa-chip-row"
            style="margin-top: 10px"
          >
            <button
              v-for="view in savedViews"
              :key="view.name"
              type="button"
              class="qa-chip"
              @click="applySavedView(view)"
            >
              {{ view.name }}
              <span
                class="qa-chip-count"
                @click.stop="deleteSavedView(view.name)"
                >×</span
              >
            </button>
          </div>
        </details>
      </div>

      <div class="qa-content qa-content--tight">
        <div class="qa-meta-strip">
          <span
            >{{ t('QA_DASHBOARD.KEYWORD.TOTAL_MATCHES') }}:
            <strong>{{ totalMatches }}</strong></span
          >
          <span
            >{{ t('QA_DASHBOARD.KEYWORD.PAGE') }}:
            <strong>{{ meta.page }} / {{ meta.total_pages }}</strong></span
          >
          <span
            >{{ t('QA_DASHBOARD.KEYWORD.AGENTS_FOUND') }}:
            <strong>{{ agentBreakdown.length }}</strong></span
          >
        </div>

        <section class="qa-card">
          <div class="qa-card-header">
            <h2 class="qa-card-title">
              {{ t('QA_DASHBOARD.KEYWORD.RESULTS') }}
            </h2>
            <div class="qa-actions qa-actions--compact">
              <button
                type="button"
                class="qa-btn qa-btn--sm"
                :disabled="page <= 1 || isLoading"
                @click="goPage(page - 1)"
              >
                {{ t('QA_DASHBOARD.KEYWORD.PREV') }}
              </button>
              <button
                type="button"
                class="qa-btn qa-btn--sm"
                :disabled="page >= meta.total_pages || isLoading"
                @click="goPage(page + 1)"
              >
                {{ t('QA_DASHBOARD.KEYWORD.NEXT') }}
              </button>
            </div>
          </div>
          <div class="qa-table-wrap">
            <table class="qa-table">
              <thead>
                <tr>
                  <th class="qa-th">
                    {{ t('QA_DASHBOARD.TABLE.CONVERSATION') }}
                  </th>
                  <th class="qa-th">{{ t('QA_DASHBOARD.KEYWORD.AGENT') }}</th>
                  <th class="qa-th">
                    {{ t('QA_DASHBOARD.KEYWORD.MATCHED_AT') }}
                  </th>
                  <th class="qa-th">{{ t('QA_DASHBOARD.KEYWORD.PREVIEW') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="isLoading">
                  <td class="qa-td qa-td--empty" colspan="4">
                    <div class="qa-loading-row">
                      <div class="qa-spinner" />
                      <span>{{ t('QA_DASHBOARD.FORM.RUNNING') }}</span>
                    </div>
                  </td>
                </tr>
                <tr v-for="row in rows" :key="row.id">
                  <td class="qa-td">
                    <RouterLink
                      v-if="row.conversationRoute"
                      class="qa-link"
                      :to="row.conversationRoute"
                    >
                      #{{ row.conversationId }}
                    </RouterLink>
                    <span v-else>#{{ row.conversationId || '—' }}</span>
                    <div class="qa-subtle">
                      {{ t('QA_DASHBOARD.KEYWORD.MESSAGE_ID') }}:
                      {{ row.messageIdLabel }}
                    </div>
                  </td>
                  <td class="qa-td">{{ row.agentName }}</td>
                  <td class="qa-td">{{ row.matchedAt }}</td>
                  <td class="qa-td qa-td--pre">
                    <RouterLink
                      v-if="row.conversationRoute"
                      class="qa-link"
                      :to="row.conversationRoute"
                    >
                      {{ row.preview }}
                    </RouterLink>
                    <span v-else>{{ row.preview }}</span>
                  </td>
                </tr>
                <tr v-if="!rows.length && !isLoading">
                  <td class="qa-td qa-td--empty" colspan="4">
                    {{ t('QA_DASHBOARD.TABLE.EMPTY') }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import '../styles/qaShell.css';
</style>
